import { LineChart, LineChartSeries } from "@mantine/charts";
import { Group, Skeleton, Stack, useMantineColorScheme, useMatches } from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "../EmptyState";
import { TimeframeInput } from "../TimeframeInput";
import { PerformanceChartTooltip } from "./components";
import ConditionsSelector from "./components/ConditionsSelector/ConditionsSelector";
import PerformanceMobileStatus from "./components/ConditionsSelector/MobileConditionsSelector";
import { CustomLegend } from "./components/CustomLegend/CustomLegend";
import {
  createChartConfig,
  createTimeframeHandler,
  generateBenchmarkDropdown,
  generateChartData,
  getBenchmarkColorByName,
  getDefaultChartProps,
  initializeBenchmark,
} from "./functions/PerformanceChart.functions";
import { getDataRange, getDateFormatter, getUniqueTicks } from "./utils";

import { ChoosePlanModal } from "@/components/widgets/PlanModals";
import { defaultCurrency } from "@/lib/currency";
import { usePlansUser } from "@/services/plans/get-user-plan";

import type { Currency } from "@/types/currency";
import type { PerformanceChart as IPerformanceChart } from "@/types/performance";
import type { FC } from "react";
import type { DropdownItem } from "../Dropdown/types";
import type { ChartProps, PerformanceChartProps } from "./interfaces/PerformanceChart.types";

type PerformanceChartComponent = FC<PerformanceChartProps> & {
  Tooltip: typeof PerformanceChartTooltip;
};

const Component: PerformanceChartComponent = ({
  benchmarks = [],
  chartProps = {},
  children,
  defaultBenchmark,
  hideBenchmarkSelector = false,
  hiddenBenchmarks = [],
  restrictKpisByPlan = false,
  loading = false,
  onTimeframeChange,
  performance,
  tooltipFormatter,
  showTooltip = true,
  showConditionsTooltip = true,
  showPerformanceWhenBenchmarkSelected = true,
  compareMode = false,
  showLockerInLegend = false,
  ...props
}) => {
  const { t } = useTranslation();
  const [showPlanModal, { open: openPlanModal, close: closePlanModal }] = useDisclosure();
  const useAllCarsLabel = compareMode && defaultBenchmark === "__allCars__";
  const allLabel = useAllCarsLabel
    ? t("components.PerformanceChart.benchmarks.allCars", "All cars")
    : t("components.PerformanceChart.benchmarks.__all__", "All conditions");
  const [currentBenchmark, setBenchmark] = useState<string | undefined>(undefined);

  const { data: plans } = usePlansUser();

  const kpisDisabled = restrictKpisByPlan && plans?.config?.garage_risk_kpis === false;

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const chartColor = isDark ? "#80c9df" : "primary";

  const isMobile = useMatches({ base: true, md: false });

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const defaultChartProps: ChartProps = getDefaultChartProps(currency);

  // Tooltip values are never rounded — always use standard notation regardless of localStorage.
  const { tooltipValueFormatter, lineChartProps } = createChartConfig(
    currency,
    defaultChartProps,
    chartProps
  );

  // If KPI filtering is enabled, and KPIs are disabled for the plan,
  // hide individual conditions and only expose the overall/average view (__all__).
  // If KPI filtering is disabled (prop=false), keep original behavior.
  var {
    filteredBenchmarks,
    dropdownBenchmarks,
    visibleName,
    hiddenSet,
  }: {
    filteredBenchmarks: IPerformanceChart[];
    dropdownBenchmarks: DropdownItem[];
    visibleName: (n?: string | number) => string;
    hiddenSet: Set<string>;
  } = generateBenchmarkDropdown(
    hiddenBenchmarks,
    benchmarks,
    restrictKpisByPlan,
    kpisDisabled,
    allLabel,
    t,
    compareMode,
    performance
  );

  // When the new behavior is enabled, always show the select (unless explicitly hidden).
  // Otherwise revert to previous behavior (show only if more than one option).
  const hasDropdown = restrictKpisByPlan
    ? !hideBenchmarkSelector && filteredBenchmarks.length > 0
    : dropdownBenchmarks.length > 1 && !hideBenchmarkSelector && filteredBenchmarks.length > 0;

  const visibleBenchmarks = restrictKpisByPlan
    ? kpisDisabled
      ? []
      : filteredBenchmarks
    : filteredBenchmarks;

  const getBenchmarkColor = getBenchmarkColorByName(visibleBenchmarks);

  const { chart, range, series } = useMemo(() => {
    let chart: Record<string, any>[] = [];
    let series: LineChartSeries[] = [];

    let cleanedChart;
    ({ cleanedChart, chart, series } = generateChartData(
      performance,
      visibleBenchmarks,
      chart,
      series,
      visibleName,
      getBenchmarkColor,
      t,
      currentBenchmark,
      compareMode,
      chartColor,
      showPerformanceWhenBenchmarkSelected,
      hiddenSet
    ));

    const cleanedSeries = series.filter((s) => !hiddenSet.has(String(s.name)));

    return { chart: cleanedChart, range: getDataRange(cleanedChart), series: cleanedSeries };
  }, [performance, visibleBenchmarks, currentBenchmark, isDark, compareMode]);

  const tickFormatter = getDateFormatter(range);
  const ticks = getUniqueTicks(
    chart.map((item) => item.date),
    tickFormatter
  );

  const hasItems =
    (performance?.data?.length ?? 0) > 0 || visibleBenchmarks.some((b) => b.data?.length > 0);

  // Track whether the original (initial) performance data contains items.
  // We only want to show the TimeframeInput when the initial dataset is non-empty.
  // Use state (not ref) so the component re-renders when data arrives.
  const [initialPerformanceHasData, setInitialPerformanceHasData] = useState<boolean | null>(null);

  useEffect(() => {
    // Initialize once. Consider the initial dataset non-empty when either the
    // `performance` payload contains data OR any visible benchmark contains data.
    if (initialPerformanceHasData !== null) return;

    const performanceLoaded = performance != null;
    if (!performanceLoaded && visibleBenchmarks.length === 0) return;

    const initialHasData =
      (performance?.data?.length ?? 0) > 0 ||
      visibleBenchmarks.some((b) => (b.data?.length ?? 0) > 0);

    setInitialPerformanceHasData(initialHasData);
  }, [performance, visibleBenchmarks, initialPerformanceHasData]);

  // Show the TimeframeInput only if a handler is provided AND the initial
  // performance data (before any timeframe filtering) contained items.
  const showTimeframeInput = !!onTimeframeChange && (initialPerformanceHasData ?? false);

  const handleTimeframeChange = createTimeframeHandler(onTimeframeChange);

  const setDefaultBenchmark = initializeBenchmark(
    currentBenchmark,
    compareMode,
    defaultBenchmark,
    setBenchmark,
    dropdownBenchmarks
  );

  // Show the group menu if there's a dropdown or if timeframe changes are enabled.
  // Keep the timeframe input visible when `onTimeframeChange` exists,
  // even if there are no data points for the current selection.
  const hasChartData =
    (performance?.data?.length ?? 0) > 0 ||
    visibleBenchmarks.some((b) => (b.data?.length ?? 0) > 0);

  const hasGroupMenu = hasChartData && (hasDropdown || !!onTimeframeChange);

  useEffect(() => {
    setDefaultBenchmark();
  }, [benchmarks, restrictKpisByPlan, defaultBenchmark, visibleBenchmarks]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayedValue = useMemo(() => {
    if (currentBenchmark) {
      const item = dropdownBenchmarks.find((b) => b.value === currentBenchmark);
      if (item) return item.label;
    }
    return allLabel;
  }, [currentBenchmark, dropdownBenchmarks, t]);

  // Select value must be the option `value`, not the label. When no benchmark
  // is selected, fall back to `__all__` if available so the Select shows correctly.
  const selectValue =
    currentBenchmark ??
    (dropdownBenchmarks.find((d) => d.value === "__all__") ? "__all__" : undefined);

  return (
    <Skeleton visible={loading}>
      <Stack {...props} pos="relative">
        {hasGroupMenu && (
          <Group justify={hasDropdown ? "space-between" : "flex-end"}>
            {hasDropdown &&
              (isMobile ? (
                <PerformanceMobileStatus
                  kpisDisabled={kpisDisabled}
                  showConditionsTooltip={showConditionsTooltip}
                  selectValue={selectValue}
                  displayedValue={displayedValue}
                  dropdownBenchmarks={dropdownBenchmarks}
                  setBenchmark={setBenchmark}
                  currentBenchmark={currentBenchmark}
                />
              ) : (
                <>
                  <ConditionsSelector
                    currentBenchmark={currentBenchmark}
                    dropdownBenchmarks={dropdownBenchmarks}
                    kpisDisabled={kpisDisabled}
                    selectValue={selectValue}
                    setBenchmark={setBenchmark}
                    showConditionsTooltip={showConditionsTooltip}
                  />
                </>
              ))}
            {showTimeframeInput && (
              <TimeframeInput
                label={t("components.PerformanceChart.timeframe")}
                defaultValue="all"
                onChange={handleTimeframeChange}
                inputProps={{
                  placeholder: t("components.PerformanceChart.timeframe_placeholder"),
                  miw: 200,
                }}
                maxDate={new Date()}
                type="month"
                valueFormat="MMM YYYY"
              />
            )}
          </Group>
        )}
        {children}
        {hasItems ? (
          <>
            <style>{`.car-performance-chart .recharts-line path, .car-performance-chart .recharts-curve { stroke-opacity: 0.75; } .car-performance-chart .recharts-area .recharts-curve { fill-opacity: 0.08; }`}</style>
            <LineChart
              data={chart}
              className="car-performance-chart"
              dataKey="date"
              dotProps={{ r: chart.length === 1 ? 2 : 0 }}
              series={series}
              strokeDasharray="0"
              tickLine="none"
              xAxisProps={{
                tickFormatter,
                ticks,
              }}
              tooltipProps={{
                content: ({ label, payload }) => {
                  const visible = visibleName(label);
                  return (
                    <PerformanceChartTooltip
                      dataKeyFormatter={(l) =>
                        t(
                          `components.PerformanceChart.benchmarks.${visibleName(l)}`,
                          visibleName(l)
                        )
                      }
                      label={tooltipFormatter ? tooltipFormatter(visible) : visible}
                      payload={payload ? [...payload] : undefined}
                      valueFormatter={tooltipValueFormatter}
                      kpisDisabled={kpisDisabled}
                      disabledBenchmarks={kpisDisabled ? filteredBenchmarks : []}
                    />
                  );
                },
              }}
              activeDotProps={{ r: 2 }}
              {...lineChartProps}
              withLegend={false}
            />

            {/* External custom legend to avoid double-rendering and allow precise placement */}
            <Group justify="flex-end" style={{ paddingTop: 8, paddingRight: 28 }}>
              <CustomLegend
                payload={series.map((s) => ({
                  value: String(s.name),
                  color: (s as any).color,
                  payload: {
                    payload: {
                      label: (s as any).label ?? s.name,
                    },
                  },
                }))}
                performanceName={performance?.name}
                showLockerInLegend={showLockerInLegend}
                openPlanModal={openPlanModal}
                getBenchmarkColor={getBenchmarkColor}
              />
            </Group>
            {showLockerInLegend && (
              <ChoosePlanModal
                isOpen={showPlanModal}
                onClose={() => closePlanModal()}
                title={t("plans.changePlanAnytime")}
              />
            )}
          </>
        ) : (
          <EmptyState type="noData" {...props} text={t("common.noResults")} />
        )}
      </Stack>
    </Skeleton>
  );
};

Component.Tooltip = PerformanceChartTooltip;

export const PerformanceChart = Component;
